// "How current is today?" — the pure decision behind the reporting dashboard's
// freshness note. LEAF (types only), `now` injected, no clock read.
//
// ⚠️⚠️ WHY THIS EXISTS. Even with the pull window fixed (2026-08-27), "Today" is
// only ever current to the LAST PULL — the Meta job runs 2-hourly, so at 09:30 the
// screen shows spend up to 08:00 with nothing saying so. And an early morning with
// genuinely no spend yet is INDISTINGUISHABLE from a period nobody has pulled yet.
// That is CLAUDE.md rule 15 applied to a period rather than a bucket: an unmeasured
// stretch rendered as a zero says "you got nothing", which is a different and worse
// claim than "we haven't looked yet".
//
// ⚠️ `ReportingSummary.asOf` cannot answer it. asOf is MAX(pulled_at) across the
// rows IN SCOPE, so an empty Today has no asOf at all — it goes quiet in exactly
// the case that needs explaining. The signal here comes from `reporting_runs` (the
// pull's own telemetry) and survives an empty scope.
//
// ⚠️ META ONLY. `reporting_runs` describes the Meta pull job. Google Ads arrives via
// the BigQuery Data Transfer on a different cadence, so putting this note on that
// dashboard would be a confidently wrong stamp — worse than none at all.

import type { DateRange } from '../reportingDateRange.js';
import type { ReportingDataFreshness } from '../../types/reporting.js';

// A range CONTAINS the viewer's local today. String compare is exact for
// YYYY-MM-DD. A range entirely in the future contains no today (→ false), which is
// why this is containment rather than `to >= today`.
export function rangeIncludesToday(range: DateRange, now: Date): boolean {
  if (!range.from || !range.to) return false;
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const today = `${y}-${m}-${d}`;
  return range.from <= today && today <= range.to;
}

// ⚠️ TWO UNKNOWN REASONS, DELIBERATELY NOT COLLAPSED. "The pull has not landed
// cleanly in over a day" is a PROBLEM; "we couldn't read the freshness telemetry"
// is a transient read failure with data that may be perfectly current. One sentence
// covering both would either raise a false alarm or understate a real outage.
export type PullFreshnessNotice =
  // ⚠️⚠️ `lastRunFinishedAt` IS JOB LIVENESS, NOT A CLEAN RUN, and `accountsBehind`
  // is what keeps that honest — see the fix note below.
  | { kind: 'as-at'; lastRunFinishedAt: string; accountsBehind: number }
  | { kind: 'unknown'; reason: 'no-successful-run' | 'unavailable' };

// Whether to say anything about pull freshness, and what. `null` = say nothing:
// either the range is complete history (no today in it, so the last pull tells the
// reader nothing new), or the freshness read simply hasn't come back yet — a
// momentary "we don't know" flash on every page load would train people to ignore
// the line that matters.
export function resolvePullFreshnessNotice(args: {
  range: DateRange;
  now: Date;
  // `undefined` = not loaded yet. A loaded value with a null lastSuccessfulAt is a
  // real answer ("no clean run on record"), not a missing one.
  freshness: ReportingDataFreshness | undefined;
  unavailable: boolean; // the freshness read failed
}): PullFreshnessNotice | null {
  const { range, now, freshness, unavailable } = args;
  if (!rangeIncludesToday(range, now)) return null;
  if (unavailable) return { kind: 'unknown', reason: 'unavailable' };
  if (freshness === undefined) return null;
  // ⚠️⚠️ THIS READS JOB LIVENESS, NOT `lastSuccessfulAt`. Fixed 2026-09-16.
  //
  // `lastSuccessfulAt` requires a run with ZERO failed accounts, and only 185 of
  // 358 Meta refresh runs over 30 days clear that bar. On 2026-09-15 this caption
  // said "as at 04:13" while the pull had finished at 22:12 — eighteen hours out,
  // live, on the line whose entire job is to be trusted about time.
  //
  // ⚠️ THE SWAP ALONE WOULD HAVE BEEN WRONG. Last-run-finished overclaims for the
  // accounts that failed in it, so the notice carries the COUNT and the sentence
  // says so. Honest in both directions, and no per-account rendering needed for
  // what is a caption.
  //
  // ⚠️ The 'no-successful-run' branch now keys on job liveness too: "nothing has
  // finished at all" is the real alarm. A run that finished with failures is NOT
  // that case — it gets the as-at line with a count, which is more informative
  // than the blanket warning it used to trigger.
  if (freshness.lastRunFinishedAt === null) return { kind: 'unknown', reason: 'no-successful-run' };
  return {
    kind: 'as-at',
    lastRunFinishedAt: freshness.lastRunFinishedAt,
    accountsBehind: freshness.accountsBehindCount ?? 0,
  };
}

// The sentence itself. ⚠️ PURE, and deliberately not left in the component — the
// `conversionNote` precedent in DisplayCurrencySelect.tsx: the wording of an
// honesty caveat is the part worth pinning, and pinning it here costs no jsdom.
// (It also matters practically: this suite sits close enough to its per-test
// timeout that one extra jsdom file tips several heavy render tests over.)
//
// The instant is formatted by the CALLER — locale and timezone are a render
// concern — and passed as a formatter rather than a pre-formatted string, so
// there is no second argument that has to correspond to the first.
export function pullFreshnessMessage(
  notice: PullFreshnessNotice,
  formatInstant: (iso: string) => string,
): string {
  if (notice.kind === 'as-at') {
    const base =
      'this range includes today, and today is only current to the last meta pull — as at ' +
      `${formatInstant(notice.lastRunFinishedAt)}. anything spent since then isn't in these figures yet.`;
    // ⚠️ THE SECOND CLAUSE IS WHAT STOPS THE FIRST OVERCLAIMING. Without it the
    // line asserts that every account is current to that instant, which is false
    // for exactly the accounts that failed in that run.
    if (notice.accountsBehind > 0) {
      const n = notice.accountsBehind;
      return (
        `${base} ${n} account${n === 1 ? '' : 's'} failed in that pull, so ` +
        `${n === 1 ? 'its figures are' : 'their figures are'} current to an earlier one.`
      );
    }
    return base;
  }
  // ⚠️ Both unknowns SAY they are unknown and carry no time. Rendering nothing
  // would hand the reader a partial figure with no caveat at all; rendering a time
  // we do not have would be worse than either.
  if (notice.reason === 'no-successful-run') {
    return (
      'this range includes today, and no meta pull has finished in the recent runs — ' +
      "so today's figures may be missing spend, and we can't say how much. check sync health."
    );
  }
  return (
    "this range includes today, and we couldn't check when meta data was last pulled — " +
    "so today's figures may be missing spend."
  );
}
